import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Eyebrow from '../components/common/Eyebrow';
import { useUI } from '../context/UIContext';
import { useAuth } from '../context/AuthContext';
import { useCreateTicketMutation } from '../store/supportApi';
import {
  useListIssueCategoriesQuery,
  useGetContactInfoQuery,
} from '../store/siteContentApi';

/* ════════════════════════ DATA ════════════════════════ */

// Issue categories + contact info are now admin-editable — they load from
// the backend (/api/issue-categories and /api/contact-info). Defaults below
// are only used as a no-flash fallback while the initial API request is in
// flight; the admin panel is the source of truth.
const CONTACT_DEFAULTS = {
  phone: '74400 75400',
  whatsapp: '+919014638633',
  email: 'support@mytrustlab.com',
  support_hours: '6:00 AM – 10:00 PM daily',
};

// Normalise the WhatsApp number into a wa.me-safe digit string so admins
// can type it in any format ("+91 90146 38633", "90146-38633", etc.).
const waHref = (whatsapp) => {
  const digits = String(whatsapp || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
};

// Display-friendly WhatsApp (strip +91 prefix if the rest is 10 digits, else show as-is).
const waDisplay = (whatsapp) => {
  const raw = String(whatsapp || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    const local = digits.slice(2);
    return `${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return raw;
};

const SLA_ROWS = [
  { ico: '🛵', label: 'Late Trustian (>60 min)', value: 'No collection charge', sub: 'For that visit' },
  { ico: '📋', label: 'Wrong / missing report', value: 'Resolved in 2 hrs', sub: 'Free re-processing' },
  { ico: '🩸', label: 'Re-collection needed', value: 'Free re-collection', sub: 'Scheduled within 4 hrs' },
  { ico: '💳', label: 'Refund request', value: '3–5 business days', sub: 'To original payment method' },
  { ico: '💬', label: 'General complaints', value: 'Acknowledged in 1 hr', sub: 'Resolved within 24 hrs' },
];

const ESCALATION_STEPS = [
  { step: 1, title: 'Front-line Support', desc: 'WhatsApp, call, or submit the form above. Our support executives handle most issues within 1–4 hours.', timing: 'Response: <4 hrs', color: 'var(--sage)' },
  { step: 2, title: 'Senior Support Manager', desc: 'If unresolved after 4 hours, ask to escalate to a Senior Manager. Email escalation@mytrustlab.com with your ticket number.', timing: 'Response: <12 hrs', color: 'var(--amber)' },
  { step: 3, title: 'Chief of Customer Experience', desc: 'For unresolved critical issues, write to ceo-office@mytrustlab.com. This inbox is reviewed by the leadership team personally.', timing: 'Response: <24 hrs', color: 'var(--forest)' },
];

const RESPONSE_INDICATORS = [
  { color: '#25D366', label: 'WhatsApp: Immediate response', blink: true },
  { color: 'var(--sage-l)', label: 'Phone: 9:30AM – 6PM daily', blink: false },
  { color: 'var(--amber)', label: 'Email: within 4 hours', blink: false },
];

/* ═══════════ Shared wrapper that matches HTML "max-width:1300 + padding:56px" ═══════════ */
const Wrap = ({ children, sx = {} }) => (
  <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 2.5, md: 7 }, ...sx }}>
    {children}
  </Box>
);

/* ═══════════ Custom form field label ═══════════ */
const InputLabel = ({ children }) => (
  <Typography
    component="label"
    sx={{
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'var(--charcoal)',
      display: 'block',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

/* ═══════════ Custom text field — clean white card-style input ═══════════ */
const StyledInput = ({ sx: sxProp, inputProps: ipProp, InputProps: IPProp, ...rest }) => (
  <TextField
    fullWidth
    variant="outlined"
    InputProps={{
      ...IPProp,
      sx: {
        borderRadius: '12px',
        fontSize: 14.5,
        bgcolor: '#fff',
        fontFamily: "'Outfit', sans-serif",
        transition: 'all 0.18s',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--border-l)',
          borderWidth: '1.5px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--sage)',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 4px rgba(61,122,82,0.10)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--sage)',
          borderWidth: '1.5px',
        },
        ...IPProp?.sx,
      },
    }}
    inputProps={{
      style: {
        padding: '14px 16px',
        fontFamily: "'Outfit', sans-serif",
        fontSize: 14.5,
        color: 'var(--ink)',
      },
      ...ipProp,
    }}
    sx={{
      '& .MuiInputLabel-root': { display: 'none' },
      // Match height for single-line vs select
      '& .MuiSelect-select': { padding: '14px 16px !important' },
      ...sxProp,
    }}
    {...rest}
  />
);

/* ═══════════ WhatsApp SVG icon (reusable) ═══════════ */
const WaSvg = ({ size = 22, fill = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   SUPPORT PAGE
   ═══════════════════════════════════════════════════════ */
// Must match OTHER_SUBCATEGORY in backend/src/controllers/supportController.js
// and the constant of the same name in the medical portal's RaiseQuery.js.
// All three have to agree or an "Other" sub-category arrives as a literal key.
const OTHER_SUBCATEGORY = '__other';

// Limits mirror the server's (MAX_ATTACHMENTS / MAX_ATTACHMENT_MB in
// backend/src/controllers/supportController.js). Checking here too is not
// belt-and-braces for its own sake: it tells the customer immediately rather
// than after a round trip that ends in a rejected complaint.
const MAX_FILES = 5;
const MAX_FILE_MB = 5;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

const prettyBytes = (n) => (n < 1024 * 1024
  ? `${Math.max(1, Math.round(n / 1024))} KB`
  : `${(n / (1024 * 1024)).toFixed(1)} MB`);

const fileKey = (f) => `${f.name}::${f.size}`;

const TicketAttachments = ({ files, onFiles }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [previews, setPreviews] = useState({});

  // A blob URL per image, revoked when the list changes or we unmount.
  // Skipping the revoke strands every preview in memory for the life of the
  // tab, and swapping a photo twice is the normal case here.
  useEffect(() => {
    const made = {};
    for (const f of files) {
      if (f.type?.startsWith('image/')) made[fileKey(f)] = URL.createObjectURL(f);
    }
    setPreviews(made);
    return () => { for (const url of Object.values(made)) URL.revokeObjectURL(url); };
  }, [files]);

  const add = (incoming) => {
    setError('');
    const picked = Array.from(incoming || []);
    if (picked.length === 0) return;

    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    const seen = new Set(files.map(fileKey));
    const fresh = picked.filter(
      (f) => f.size <= MAX_FILE_MB * 1024 * 1024 && !seen.has(fileKey(f)),
    );
    const room = MAX_FILES - files.length;

    if (tooBig) setError(`${tooBig.name} is over ${MAX_FILE_MB} MB.`);
    else if (fresh.length > room) setError(`Up to ${MAX_FILES} files.`);

    if (room > 0) onFiles([...files, ...fresh.slice(0, room)]);
  };

  const atLimit = files.length >= MAX_FILES;

  return (
    <Box>
      <Box
        onClick={() => !atLimit && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (!atLimit && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => { if (!atLimit) e.preventDefault(); }}
        onDrop={(e) => { if (!atLimit) { e.preventDefault(); add(e.dataTransfer.files); } }}
        sx={{
          px: 2, py: 2,
          border: '1.5px dashed rgba(0,0,0,.16)',
          borderRadius: '10px',
          textAlign: 'center',
          cursor: atLimit ? 'not-allowed' : 'pointer',
          opacity: atLimit ? 0.55 : 1,
          transition: 'border-color .15s, background .15s',
          '&:hover': !atLimit && { borderColor: 'var(--sage)', bgcolor: 'rgba(0,0,0,.015)' },
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>
          {atLimit ? `Attachment limit reached (${MAX_FILES})` : 'Click to upload or drag & drop'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--stone)', mt: 0.25 }}>
          JPG, PNG or PDF · up to {MAX_FILE_MB} MB each · max {MAX_FILES} files
        </Typography>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => { add(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      </Box>

      {error && (
        <Typography sx={{ fontSize: 12, color: 'var(--red)', mt: 0.75 }}>{error}</Typography>
      )}

      {files.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
          {files.map((file, i) => (
            <Box
              key={`${fileKey(file)}-${i}`}
              sx={{
                position: 'relative', width: 96,
                border: '1px solid rgba(0,0,0,.12)', borderRadius: '10px', overflow: 'hidden',
              }}
            >
              <Box sx={{ height: 62, bgcolor: 'rgba(0,0,0,.04)', display: 'grid', placeItems: 'center' }}>
                {previews[fileKey(file)] ? (
                  <Box
                    component="img"
                    src={previews[fileKey(file)]}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--stone)' }}>PDF</Typography>
                )}
              </Box>
              <Box sx={{ px: 0.75, py: 0.5 }}>
                <Typography noWrap sx={{ fontSize: 10.5, color: 'var(--ink)' }}>{file.name}</Typography>
                <Typography sx={{ fontSize: 9.5, color: 'var(--stone)' }}>
                  {prettyBytes(file.size)}
                </Typography>
              </Box>
              <Box
                onClick={(e) => { e.stopPropagation(); onFiles(files.filter((_, idx) => idx !== i)); }}
                role="button"
                aria-label={`Remove ${file.name}`}
                sx={{
                  position: 'absolute', top: 4, right: 4,
                  width: 20, height: 20, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,.6)', color: '#fff',
                  display: 'grid', placeItems: 'center',
                  fontSize: 12, lineHeight: 1, cursor: 'pointer',
                  '&:hover': { bgcolor: 'var(--red)' },
                }}
              >
                ×
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const Support = () => {
  const { showToast } = useUI();
  const { user } = useAuth();

  // Admin-editable data.
  // The endpoint returns { categories, subcategories } — NOT a bare array.
  // Destructuring `data` straight into `categories` handed this component an
  // OBJECT, so `categories.map` threw and the whole page rendered blank.
  // `?? []` cannot help there: the object is not null, it is just the wrong
  // shape. See listActiveCategories in
  // backend/src/controllers/issueCategoryController.js.
  const { data: issueTypes } = useListIssueCategoriesQuery();
  const categories = useMemo(() => issueTypes?.categories ?? [], [issueTypes]);
  const subcategories = useMemo(() => issueTypes?.subcategories ?? [], [issueTypes]);
  const { data: contactFromApi } = useGetContactInfoQuery();
  const contact = contactFromApi || CONTACT_DEFAULTS;

  const [activeCat, setActiveCat] = useState('');
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    bookingId: '',
    category: '',
    subcategory: '',
    subcategoryOther: '',
    priority: 'normal',
    message: '',
    attachments: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  // Sub-categories belonging to the chosen category. Empty when that
  // category has none — an admin can add them to one category without being
  // forced to add them to all of them, and an empty dropdown reads as
  // broken, so the field only renders when there is something in it.
  const subOptions = useMemo(
    () => (form.category ? subcategories.filter((sc) => sc.parent_key === form.category) : []),
    [subcategories, form.category],
  );

  // Changing the category invalidates whatever sub-category was picked: it
  // belonged to the category that was just replaced.
  useEffect(() => {
    setForm((f) => (
      f.subcategory || f.subcategoryOther
        ? { ...f, subcategory: '', subcategoryOther: '' }
        : f
    ));
  }, [form.category]);

  // Once categories load, pick the first as the default selection so the
  // grid + dropdown aren't empty.
  useEffect(() => {
    if (!categories.length) return;
    setActiveCat((cur) => cur || categories[0].key);
    setForm((f) => (f.category ? f : { ...f, category: categories[0].key }));
  }, [categories]);

  // Prefill name + phone once the logged-in user becomes available (e.g. after
  // login on another tab, or if the context hydrates after first paint).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || '',
      phone: f.phone || user.phone || '',
    }));
  }, [user]);

  const update = useCallback(
    (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
    [],
  );

  const selectIssue = useCallback((key) => {
    setActiveCat(key);
    setForm((f) => ({ ...f, category: key }));
  }, []);

  const [createTicket, { isLoading: submitting }] = useCreateTicketMutation();

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.name || !form.phone || !form.category || !form.message) {
        showToast('Please fill all required fields', 'warning');
        return;
      }
      // "Other" with nothing typed carries no meaning — the whole point of
      // the option is the words that follow it.
      if (form.subcategory === OTHER_SUBCATEGORY && !form.subcategoryOther.trim()) {
        showToast('Please tell us what the issue is', 'warning');
        return;
      }
      try {
        const res = await createTicket({
          name: form.name.trim(),
          phone: form.phone.trim(),
          booking_id: form.bookingId?.trim() || undefined,
          category: form.category,
          subcategory: form.subcategory || '',
          subcategory_other:
            form.subcategory === OTHER_SUBCATEGORY ? form.subcategoryOther.trim() : '',
          priority: form.priority,
          message: form.message.trim(),
          // Only present when files were picked; createTicket switches to
          // multipart on this and posts plain JSON otherwise.
          attachments: form.attachments,
        }).unwrap();
        const tid = res?.ticket?.ticket_id || '';
        setTicketId(tid);
        setSubmitted(true);
        showToast(`Complaint raised — reference ${tid}`);
      } catch (err) {
        showToast(err?.data?.message || 'Could not submit — please try again', 'error');
      }
    },
    [form, showToast, createTicket],
  );

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <Box>

      {/* ═══════════════════ 1. HERO ═══════════════════ */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          pt: { xs: 10, md: 12 },
          pb: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* radial accent */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 60% 80% at 80% 50%,rgba(61,122,82,.2) 0%,transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Wrap sx={{ position: 'relative', zIndex: 2 }}>
          {/* centred hero content */}
          <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', pb: 7 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Eyebrow white>Customer Support</Eyebrow>
            </Box>

            <Typography
              className="serif"
              variant="h1"
              sx={{ color: '#fff', mt: 2, mb: 2.25 }}
            >
              We&apos;re here for you.
              <br />
              <Box component="em" sx={{ color: 'var(--sage-l)', fontStyle: 'italic' }}>
                Always.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: 17,
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 300,
                lineHeight: 1.8,
                mb: 4.5,
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              Had an issue with your collection, report, or booking? Tell us and
              we&apos;ll make it right — fast. A real person responds on WhatsApp
              immediately.
            </Typography>

            {/* CTA buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{justifyContent:"center"}}
            >
              <Button
                variant="contained"
                href={waHref(contact.whatsapp)}
                target="_blank"
                startIcon={<WaSvg size={20} fill="currentColor" />}
                sx={{
                  bgcolor: '#25D366',
                  color: '#fff',
                  borderRadius: '100px',
                  fontWeight: 700,
                  fontSize: 15,
                  px: 3.5,
                  py: 1.5,
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(37,211,102,0.35)',
                  '&:hover': { bgcolor: '#1da851' },
                }}
              >
                Chat on WhatsApp
              </Button>

              <Button
                variant="outlined"
                href={`tel:${(contact.phone || '').replace(/\s/g, '')}`}
                sx={{
                  borderRadius: '100px',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 700,
                  fontSize: 15,
                  px: 3.5,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    borderColor: 'rgba(255,255,255,0.4)',
                  },
                }}
              >
                📞 Call {contact.phone}
              </Button>
            </Stack>
          </Box>

          {/* response-time strip (full-bleed inside the container) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              py: 2.5,
              flexWrap: 'wrap',
            }}
          >
            {RESPONSE_INDICATORS.map((r, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  px: { xs: 1.5, md: 4 },
                  borderRight:
                    i < RESPONSE_INDICATORS.length - 1
                      ? '1px solid rgba(255,255,255,0.08)'
                      : 'none',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: r.color,
                    borderRadius: '50%',
                    ...(r.blink && {
                      animation: 'supportBlink 1.4s ease-in-out infinite',
                      '@keyframes supportBlink': {
                        '0%,100%': { opacity: 1 },
                        '50%': { opacity: 0.2 },
                      },
                    }),
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: 11, md: 13 },
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600,
                  }}
                >
                  {r.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Wrap>
      </Box>

      {/* ═══════════════════ 2. ISSUE CATEGORIES + FORM ═══════════════════ */}
      <Box sx={{ bgcolor: 'var(--cream)', py: { xs: 6, md: 9 } }}>
        <Wrap>
          {/* centred section heading */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              mb: { xs: 4, md: 5.5 },
            }}
          >
            <Eyebrow>What can we help with?</Eyebrow>
            <Typography
              variant="h2"
              className="serif"
              sx={{ mt: 1.5, color: 'var(--ink)' }}
            >
              Select your Query/Issue/Complaint type
            </Typography>
          </Box>

          {/* 4×2 category cards — CSS grid for precise 4-per-row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: { xs: '12px', md: '14px' },
              mb: { xs: 4, md: 6 },
            }}
          >
            {categories.map((cat) => {
              const active = activeCat === cat.key;
              return (
                <Box
                  key={cat.key}
                  onClick={() => selectIssue(cat.key)}
                  sx={{
                    bgcolor: active ? 'var(--mint)' : '#fff',
                    border: `1.5px solid ${active ? 'var(--sage)' : 'var(--border-l)'}`,
                    borderRadius: '18px',
                    p: { xs: 2, md: 2.75 },
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'var(--sage)',
                      bgcolor: 'var(--mint)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 4px 16px rgba(61,122,82,0.08)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 28, mb: 1.25, lineHeight: 1 }}>
                    {cat.icon}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 0.5 }}
                  >
                    {cat.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'var(--stone)',
                      fontWeight: 300,
                      lineHeight: 1.5,
                    }}
                  >
                    {cat.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ─── two-column: form (left) + sidebar (right) ─── */}
          <Grid container sx={{justifyContent:'center'}} spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
            {/* LEFT — complaint form */}
            <Grid item xs={12} md={7}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid var(--border-l)',
                  borderRadius: '20px',
                  p: { xs: 3, md: 4.5 },
                  boxShadow: '0 4px 20px rgba(21,42,30,0.06)',
                }}
              >
                {!submitted ? (
                  <>
                    <Typography
                      className="serif"
                      sx={{
                        fontSize: { xs: 20, md: 24 },
                        fontWeight: 600,
                        color: 'var(--ink)',
                        mb: 0.75,
                      }}
                    >
                      Raise a complaint or request
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, color: 'var(--stone)', mb: 3.5, fontWeight: 300 }}
                    >
                      We aim to resolve every issue within{' '}
                      <Box component="strong" sx={{ color: 'var(--sage)', fontWeight: 700 }}>
                        4 hours
                      </Box>
                      . For urgent matters, please use WhatsApp.
                    </Typography>

                    <Stack spacing={2}>
                      {/* Name */}
                      <Box>
                        <InputLabel>Your Name *</InputLabel>
                        <StyledInput
                          placeholder="Full name"
                          value={form.name}
                          onChange={update('name')}
                          required
                        />
                      </Box>

                      {/* Phone + Booking ID */}
                      <Grid container spacing={1.75}>
                        <Grid item xs={12} sm={6}>
                          <InputLabel>Mobile Number *</InputLabel>
                          <StyledInput
                            placeholder="+91 XXXXXXXXXX"
                            value={form.phone}
                            onChange={update('phone')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InputLabel>Booking ID (if any)</InputLabel>
                          <StyledInput
                            placeholder="TL-2026-XXXXX"
                            value={form.bookingId}
                            onChange={update('bookingId')}
                          />
                        </Grid>
                      </Grid>

                      {/* Category */}
                      <Box>
                        <InputLabel>Issue Category *</InputLabel>
                        <StyledInput
                          select
                          value={form.category}
                          onChange={update('category')}
                          required
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (v) => {
                              const c = categories.find((x) => x.key === v);
                              return c ? c.title : '— Select issue type —';
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            — Select issue type —
                          </MenuItem>
                          {categories.map((c) => (
                            <MenuItem key={c.key} value={c.key}>
                              {c.icon ? `${c.icon}  ` : ''}{c.title}
                            </MenuItem>
                          ))}
                        </StyledInput>
                      </Box>

                      {/* Sub-category — only when the chosen category has
                          any. An empty dropdown looks broken, and an admin
                          may add sub-categories to one category without
                          adding them to all of them. */}
                      {subOptions.length > 0 && (
                        <Box>
                          <InputLabel>What specifically?</InputLabel>
                          <StyledInput
                            select
                            value={form.subcategory}
                            onChange={update('subcategory')}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (v) => {
                                if (v === OTHER_SUBCATEGORY) return 'Other';
                                const sc = subOptions.find((x) => x.key === v);
                                return sc ? sc.title : '— Optional —';
                              },
                            }}
                          >
                            <MenuItem value="">— Optional —</MenuItem>
                            {subOptions.map((sc) => (
                              <MenuItem key={sc.key} value={sc.key}>
                                {sc.icon ? `${sc.icon}  ` : ''}{sc.title}
                              </MenuItem>
                            ))}
                            {/* Always last. The admin list can never cover
                                every case, and without this a customer picks
                                whichever option is closest — which is worse
                                than them telling us in their own words. */}
                            <MenuItem value={OTHER_SUBCATEGORY}>Other</MenuItem>
                          </StyledInput>
                        </Box>
                      )}

                      {/* Free text, shown only after "Other" is picked. */}
                      {form.subcategory === OTHER_SUBCATEGORY && (
                        <Box>
                          <InputLabel>Tell us in a few words *</InputLabel>
                          <StyledInput
                            value={form.subcategoryOther}
                            onChange={update('subcategoryOther')}
                            placeholder="e.g. Sample collected from the wrong arm"
                            inputProps={{ maxLength: 120 }}
                            required
                          />
                        </Box>
                      )}

                      {/* Priority */}
                      <Box>
                        <InputLabel>Priority *</InputLabel>
                        <RadioGroup
                          row
                          value={form.priority}
                          onChange={update('priority')}
                          sx={{ gap: 1.25, flexWrap: 'nowrap' }}
                        >
                          {[
                            { value: 'normal', label: 'Normal', clr: 'var(--sage)' },
                            { value: 'urgent', label: 'Urgent', clr: 'var(--red)' },
                          ].map((opt) => {
                            const active = form.priority === opt.value;
                            return (
                              <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={
                                  <Radio
                                    size="small"
                                    sx={{ color: opt.clr, '&.Mui-checked': { color: opt.clr } }}
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: 13.5,
                                      fontWeight: 700,
                                      color: active ? opt.clr : 'var(--ink)',
                                    }}
                                  >
                                    {opt.label}
                                  </Typography>
                                }
                                sx={{
                                  flex: 1,
                                  bgcolor: active ? 'var(--mint, #EEF8F2)' : '#fff',
                                  border: `1.5px solid ${active ? opt.clr : 'var(--border-l)'}`,
                                  borderRadius: '12px',
                                  px: 1.5,
                                  py: 0.75,
                                  m: 0,
                                  transition: 'all 0.18s',
                                  cursor: 'pointer',
                                  '&:hover': { borderColor: opt.clr },
                                }}
                              />
                            );
                          })}
                        </RadioGroup>
                      </Box>

                      {/* Message */}
                      <Box>
                        <InputLabel>Describe the issue *</InputLabel>
                        <StyledInput
                          multiline
                          rows={4}
                          placeholder="Please describe what happened — what was expected, what actually happened, and when it occurred…"
                          value={form.message}
                          onChange={update('message')}
                          required
                        />
                      </Box>

                      {/* Attachments — optional. A photo of the report or
                          the bill answers half the questions support would
                          otherwise have to ask over the phone. */}
                      <Box>
                        <InputLabel>Attach a photo or PDF (optional)</InputLabel>
                        <TicketAttachments
                          files={form.attachments}
                          onFiles={(files) => setForm((f) => ({ ...f, attachments: files }))}
                        />
                      </Box>

                      {/* Submit */}
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{
                          bgcolor: 'var(--sage)',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 15,
                          borderRadius: '14px',
                          py: 1.75,
                          textTransform: 'none',
                          '&:hover': { bgcolor: 'var(--forest)' },
                        }}
                      >
                        {submitting ? 'Submitting…' : 'Submit →'}
                      </Button>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: 'var(--stone)',
                          textAlign: 'center',
                          fontWeight: 300,
                        }}
                      >
                        Our team will call or WhatsApp you within 4 hours
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  /* ── success state ── */
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: 52, mb: 2 }}>✅</Typography>
                    <Typography
                      className="serif"
                      sx={{ fontSize: 26, color: 'var(--ink)', mb: 1.25 }}
                    >
                      Complaint received!
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'var(--stone)',
                        fontWeight: 300,
                        lineHeight: 1.7,
                        maxWidth: 320,
                        mx: 'auto',
                        mb: 2.5,
                      }}
                    >
                      Your ticket has been raised. Our support team will reach out
                      within{' '}
                      <Box component="strong" sx={{ color: 'var(--sage)' }}>
                        4 hours
                      </Box>
                      . Urgent issues are handled within 1 hour.
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'var(--mint)',
                        border: '1px solid var(--sage-p)',
                        borderRadius: '100px',
                        px: 2.25,
                        py: 1,
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--forest)',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {ticketId}
                    </Box>
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        href={`/my-tickets?open=${encodeURIComponent(ticketId)}`}
                        sx={{
                          bgcolor: 'var(--sage)', color: '#fff',
                          borderRadius: 100, textTransform: 'none', fontWeight: 700,
                          px: 3, py: 1,
                          '&:hover': { bgcolor: 'var(--forest)' },
                        }}
                      >
                        Track this ticket →
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* RIGHT — SLA + contact channels */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                {/* SLA commitments */}
                <Box
                  sx={{
                    bgcolor: 'var(--forest)',
                    borderRadius: '20px',
                    p: { xs: 3, md: 4 },
                  }}
                >
                  <Eyebrow white sx={{ mb: 2 }}>
                    Our Service Commitments
                  </Eyebrow>

                  {SLA_ROWS.map((row, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.75,
                        borderBottom:
                          i < SLA_ROWS.length - 1
                            ? '1px solid rgba(255,255,255,0.07)'
                            : 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 18, lineHeight: 1 }}>
                          {row.ico}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.7)',
                            fontWeight: 500,
                          }}
                        >
                          {row.label}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: i === 0 ? 'var(--amber)' : 'var(--sage-l)',
                          }}
                        >
                          {row.value}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                          {row.sub}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* WhatsApp */}
                <Box
                  component="a"
                  href={waHref(contact.whatsapp)}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: '#fff',
                    border: '1.5px solid #25D366',
                    borderRadius: '16px',
                    p: 2.25,
                    textDecoration: 'none',
                    transition: 'all 0.25s',
                    '&:hover': { bgcolor: '#F0FFF4' },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: '#25D366',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <WaSvg />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                      WhatsApp Support
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}
                    >
                      {waDisplay(contact.whatsapp)} · Fastest response
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.625,
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#25D366',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        bgcolor: '#25D366',
                        borderRadius: '50%',
                        animation: 'supportBlink 1.4s ease-in-out infinite',
                      }}
                    />
                    Online now
                  </Box>
                </Box>

                {/* Call */}
                <Box
                  component="a"
                  href={`tel:${(contact.phone || '').replace(/\s/g, '')}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: '#fff',
                    border: '1.5px solid var(--sage-p)',
                    borderRadius: '16px',
                    p: 2.25,
                    textDecoration: 'none',
                    transition: 'all 0.25s',
                    '&:hover': { bgcolor: 'var(--mint)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: 'var(--sage)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    📞
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                      Call Us
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}
                    >
                      {contact.phone} · {contact.support_hours}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12,
                      color: 'var(--sage)',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    Call now →
                  </Typography>
                </Box>

                {/* Email */}
                <Box
                  component="a"
                  href={`mailto:${contact.email || ''}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: '#fff',
                    border: '1.5px solid var(--border-l)',
                    borderRadius: '16px',
                    p: 2.25,
                    textDecoration: 'none',
                    transition: 'all 0.25s',
                    '&:hover': {
                      borderColor: 'var(--sage)',
                      bgcolor: 'var(--mint)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: 'var(--parch)',
                      border: '1.5px solid var(--border)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    ✉️
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                      Email Support
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}
                    >
                      {contact.email} · Reply in 4 hrs
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12,
                      color: 'var(--stone)',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    Send email →
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Wrap>
      </Box>

      {/* ═══════════════════ 3. ESCALATION POLICY ═══════════════════ */}
      <Box sx={{ bgcolor: 'var(--parch)', py: { xs: 6, md: 8 } }}>
        <Wrap>
          {/* centred heading */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              mb: { xs: 4, md: 5.5 },
            }}
          >
            <Eyebrow>Escalation Policy</Eyebrow>
            <Typography
              variant="h2"
              className="serif"
              sx={{ mt: 1.5, color: 'var(--ink)', mb: 1.5 }}
            >
              Not happy with the response?
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 300,
                color: 'var(--stone)',
                maxWidth: 460,
                lineHeight: 1.7,
              }}
            >
              We have a clear escalation ladder. If your issue isn&apos;t resolved
              to your satisfaction, here&apos;s how to escalate.
            </Typography>
          </Box>

          {/* stacked escalation cards — single connected block */}
          <Box sx={{ maxWidth: 820, mx: 'auto' }}>
            {/* Step 1 — top rounded */}
            {ESCALATION_STEPS.map((step, i) => (
              <Box
                key={step.step}
                sx={{
                  bgcolor: '#fff',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'var(--border-l)',
                  borderBottomWidth: 0,
                  borderRadius: i === 0 ? '20px 20px 0 0' : 0,
                  display: 'flex',
                  gap: 2.5,
                  alignItems: 'flex-start',
                  p: { xs: 2.5, md: '28px 32px' },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: step.color,
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {step.step}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', mb: 0.625 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'var(--stone)', fontWeight: 300, lineHeight: 1.65 }}>
                    {step.desc}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.25 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: step.color }}>
                    {step.timing}
                  </Typography>
                </Box>
              </Box>
            ))}


          </Box>
        </Wrap>
      </Box>
    </Box>
  );
};

export default Support;
